import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { SettingsCard, SettingsSection, SettingsToggleRow } from '../components/SettingsSection';

interface CashOnDeliverySectionProps {
  markPaidEnabled: boolean;
}

/**
 * Cash-on-delivery bookkeeping: when a COD shipment is delivered the carrier
 * has collected the payment, so the Shopify order is marked as paid. Saves
 * immediately on click.
 */
export function CashOnDeliverySection({ markPaidEnabled }: CashOnDeliverySectionProps) {
  const { t } = useTranslation('settings');

  const update = useUpdateMerchantSettings();

  const [pending, setPending] = useState<boolean | null>(null);
  const checked = pending ?? markPaidEnabled;

  useEffect(() => {
    if (pending !== null && pending === markPaidEnabled) {
      setPending(null);
    }
  }, [pending, markPaidEnabled]);

  const handleToggle = (next: boolean) => {
    if (update.isPending) return;
    setPending(next);
    update.mutate(
      { settings: { cod_mark_paid_enabled: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPending(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsSection title={t('general.cod.title')} description={t('general.cod.description')}>
      <SettingsCard>
        <SettingsToggleRow
          label={t('general.cod.markPaidLabel')}
          description={t('general.cod.markPaidDescription')}
          checked={checked}
          onCheckedChange={handleToggle}
        />
      </SettingsCard>
    </SettingsSection>
  );
}
